import React, { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Combobox, ComboboxOption } from 'modules/ui/molecules/Combobox'
import useConfig from 'modules/config/state/use-config'
import useOrder from 'modules/order/state/order/use-order'
import useDesignationSearch from './use-designation-search'
import { useGetCampaignDesignationsQuery, fetchDesignation } from 'redux/services/designation'
import useDesignationURLInitialization from 'modules/order/state/designation/use-url-designation-initialization'
import orderDesignationSlice from 'modules/order/state/designation/order-designation-slice'
import { TypedDispatch, RootState } from 'redux/store'
import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'

export default function DesignationPreConversion() {
  const { t } = useTranslation()
  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const config = useConfig()
  const order = useOrder()
  const trackUIElementEvent = useTrackUIElementEvent()

  const [searchQuery, setSearchQuery] = useState('')
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const [hasTrackedInteraction, setHasTrackedInteraction] = useState(false)

  // Initialize designation from URL if present
  useDesignationURLInitialization()

  // Initialize designation name from config default if not set
  useEffect(() => {
    const { designationId, designationName } = order.designation.value

    if (
      !hasUserInteracted &&
      designationId &&
      designationId !== -1 &&
      (!designationName || designationName === '')
    ) {
      const initializeDefaultDesignationName = async () => {
        try {
          const designationResponse = await dispatch(
            fetchDesignation.initiate({
              designationId: designationId,
            }),
          ).unwrap()

          if (designationResponse && designationResponse.id === designationId) {
            const newDesignationState = {
              designationId: designationResponse.id,
              designationName: designationResponse.name,
            }

            dispatch(orderDesignationSlice.actions.setValue(newDesignationState))
          }
        } catch (error) {
          console.error(`Failed to fetch default designation for ID ${designationId}:`, error)
        }
      }

      initializeDefaultDesignationName()
    }
  }, [
    dispatch,
    order.designation.value.designationId,
    config.designation.designationId,
    hasUserInteracted,
  ])

  // Check if multiple designations are available
  const { data: designationsResponse } = useGetCampaignDesignationsQuery({
    campaignId: config.campaignId,
    params: { per_page: 1 },
  })

  const { data, loading } = useDesignationSearch(config.campaignId, searchQuery, 1)

  const { designationId, designationName } = order.designation.value

  // Check if we're showing default text
  const urlParams = useSelector((state: RootState) => state.urlParams)
  const isFromURL = urlParams.designation === designationId

  const isDefaultText =
    (!hasUserInteracted && !isFromURL) || !designationName || designationName === ''

  // Don't render if only one or no designations available
  const shouldRender = designationsResponse && designationsResponse.total > 1

  // Convert designation data to ComboboxOption format
  const options: ComboboxOption[] = data.map((item) => ({
    value: item.value,
    label: item.label,
    isHeader: item.isHeader,
  }))

  const getDisplayText = () => {
    if (!hasUserInteracted && !isFromURL) {
      return t('CheckoutDesignationSelector.placeholder', 'Wherever needed')
    }

    if (designationName && designationName !== '') {
      return designationName
    }

    return t('CheckoutDesignationSelector.placeholder', 'Wherever needed')
  }

  const handleSelect = (value: string | number, label: string) => {
    setHasUserInteracted(true)

    // Track designation selection event
    trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
      tracking_id: 'program designation selection',
      value: label,
      designation_id: Number(value),
    })

    const designationPayload = {
      designationId: Number(value),
      designationName: label,
    }

    order.designation.setValue(designationPayload)
  }

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)

    // Track first interaction with designation search
    if (!hasTrackedInteraction && query.length > 0) {
      setHasTrackedInteraction(true)

      trackUIElementEvent('Value Selected', mParticleSdk.EventType.Other, {
        tracking_id: 'program designation start',
        value: 'designation search initiated',
      })
    }
  }

  if (!shouldRender) {
    return null
  }

  return (
    <Combobox
      fieldLabel={t('CheckoutDesignationSelector.fieldLabel', 'Use donation for:')}
      placeholder={t('CheckoutDesignationSelector.placeholder', 'Wherever needed')}
      defaultText={t('CheckoutDesignationSelector.placeholder', 'WWherever needed')}
      selectedValue={designationId}
      selectedLabel={getDisplayText()}
      options={options}
      loading={loading}
      onSelect={handleSelect}
      onSearchChange={handleSearchChange}
      searchQuery={searchQuery}
      ariaLabel={t('CheckoutDesignationSelector.ariaLabel', 'Select program designation')}
      ariaOptionsLabel={t(
        'CheckoutDesignationSelector.optionsLabel',
        'Program designation options',
      )}
      dataIdPrefix="designation-pre-conversion"
      showGroupHeaders={config.designation.showDesignationGroupName}
      noResultsText={t(
        'CheckoutDesignationSelector.noResults',
        'No programs found for "{{query}}"',
        {
          query: searchQuery,
        },
      )}
      searchPlaceholder={t(
        'CheckoutDesignationSelector.searchPlaceholder',
        'Search for a program designation',
      )}
    />
  )
}
